import { useState, useEffect, useMemo, useCallback } from 'react';
import { db, appId } from '../../../firebase-config';
import { collection, onSnapshot, doc, setDoc, getDocs, writeBatch } from 'firebase/firestore';
import {
  resolveRoutesCollectionPath,
  resolveLogisticsMonitorSettingsPath,
  resolveFleetExpenseCategoriesCollectionPath,
  resolveFleetExpensesCollectionPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyLiquidationSettingsPath,
  resolveCompanyOfficeSettingsPath,
  resolveCompanyModulesSettingsPath,
  resolveStockProductsCollectionPath,
  resolveStockPurchaseLinesCollectionPath,
  resolveStockMovementsCollectionPath
} from '../../../core/company/companyPaths';
import {
  normalizeLiquidationSettings,
  DEFAULT_LIQUIDATION_SETTINGS
} from '../../logistics/components/LogisticsUtils';
import {
  getLocalDateAsString,
  isRouteOperationallyActive,
  getNormalizedLiquidationSettings,
  buildMonitorSettings,
  sortRoutesDesc,
  sortExpensesDesc
} from '../utils/routeMonitorUtils';

const DEFAULT_SETTINGS = {
  costModel: 'perKm',
  pricePerKm: 0,
  costScales: [],
  liquidationSettings: DEFAULT_LIQUIDATION_SETTINGS
};

export default function useRouteMonitorData({ activeCompanyId, activeCompanyName, showMessage }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showHistoryDetail, setShowHistoryDetail] = useState(false);
  const [selectedHistoryRoute, setSelectedHistoryRoute] = useState(null);

  const [allRoutes, setAllRoutes] = useState([]);
  const [allFleetExpenses, setAllFleetExpenses] = useState([]);
  const [fleetCategories, setFleetCategories] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [stockProducts, setStockProducts] = useState([]);
  const [stockPurchaseLines, setStockPurchaseLines] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showYearlyHistory, setShowYearlyHistory] = useState(false);

  const [routesSource, setRoutesSource] = useState('company');

  const activeRoutes = useMemo(() => {
    const today = getLocalDateAsString(new Date());
    return allRoutes.filter((route) => isRouteOperationallyActive(route, today));
  }, [allRoutes]);

  useEffect(() => {
    if (!db || !appId || !activeCompanyId) return undefined;

    let companyMainDoc = null;
    let companyLiquidationDoc = null;
    let companyOfficeDoc = null;
    let companyModulesDoc = null;
    let companyMonitorDoc = null;
    let legacySettingsDoc = null;

    let companyMainReady = false;
    let companyLiquidationReady = false;
    let companyOfficeReady = false;
    let companyModulesReady = false;
    let companyMonitorReady = false;
    let legacySettingsReady = false;

    let companyCategoriesDocs = [];
    let legacyCategoriesDocs = [];
    let companyCategoriesReady = false;
    let legacyCategoriesReady = false;

    const companyMainRef = doc(db, resolveCompanyMainSettingsPath(activeCompanyId));
    const companyLiquidationRef = doc(db, resolveCompanyLiquidationSettingsPath(activeCompanyId));
    const companyOfficeRef = doc(db, resolveCompanyOfficeSettingsPath(activeCompanyId));
    const companyModulesRef = doc(db, resolveCompanyModulesSettingsPath(activeCompanyId));
    const companyMonitorRef = doc(db, resolveLogisticsMonitorSettingsPath(activeCompanyId));

    const legacySettingsRef = doc(
      db,
      `artifacts/${appId}/public/data/logisticsSettings`,
      'main'
    );

    const companyCategoriesRef = collection(
      db,
      resolveFleetExpenseCategoriesCollectionPath(activeCompanyId)
    );
    const legacyCategoriesRef = collection(
      db,
      `artifacts/${appId}/public/data/fleetExpenseCategories`
    );

    const syncSettings = () => {
      if (
        !companyMainReady ||
        !companyLiquidationReady ||
        !companyOfficeReady ||
        !companyModulesReady ||
        !companyMonitorReady ||
        !legacySettingsReady
      ) {
        return;
      }

      const builtSettings = buildMonitorSettings({
        companyId: activeCompanyId,
        companyName: activeCompanyName,
        mainSettings: companyMainDoc,
        liquidationSettings: companyLiquidationDoc,
        officeSettings: companyOfficeDoc,
        modulesSettings: companyModulesDoc,
        logisticsMonitorSettings: companyMonitorDoc,
        legacySettings: legacySettingsDoc
      });

      const rawSettings =
        builtSettings && typeof builtSettings === 'object'
          ? builtSettings
          : {
              costModel: 'perKm',
              pricePerKm: 0,
              costScales: []
            };

      setSettings({
        ...rawSettings,
        liquidationSettings: normalizeLiquidationSettings(rawSettings)
      });
    };

    const syncCategories = () => {
      if (!companyCategoriesReady || !legacyCategoriesReady) return;
      setFleetCategories(
        companyCategoriesDocs.length > 0 ? companyCategoriesDocs : legacyCategoriesDocs
      );
    };

    const unsubCompanyMain = onSnapshot(
      companyMainRef,
      (docSnap) => {
        companyMainReady = true;
        companyMainDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        companyMainReady = true;
        companyMainDoc = null;
        syncSettings();
      }
    );

    const unsubCompanyLiquidation = onSnapshot(
      companyLiquidationRef,
      (docSnap) => {
        companyLiquidationReady = true;
        companyLiquidationDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        companyLiquidationReady = true;
        companyLiquidationDoc = null;
        syncSettings();
      }
    );

    const unsubCompanyOffice = onSnapshot(
      companyOfficeRef,
      (docSnap) => {
        companyOfficeReady = true;
        companyOfficeDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        companyOfficeReady = true;
        companyOfficeDoc = null;
        syncSettings();
      }
    );

    const unsubCompanyModules = onSnapshot(
      companyModulesRef,
      (docSnap) => {
        companyModulesReady = true;
        companyModulesDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        companyModulesReady = true;
        companyModulesDoc = null;
        syncSettings();
      }
    );

    const unsubCompanyMonitor = onSnapshot(
      companyMonitorRef,
      (docSnap) => {
        companyMonitorReady = true;
        companyMonitorDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        companyMonitorReady = true;
        companyMonitorDoc = null;
        syncSettings();
      }
    );

    const unsubLegacySettings = onSnapshot(
      legacySettingsRef,
      (docSnap) => {
        legacySettingsReady = true;
        legacySettingsDoc = docSnap.exists() ? docSnap.data() : null;
        syncSettings();
      },
      () => {
        legacySettingsReady = true;
        legacySettingsDoc = null;
        syncSettings();
      }
    );

    const unsubCompanyCategories = onSnapshot(
      companyCategoriesRef,
      (snapshot) => {
        companyCategoriesReady = true;
        companyCategoriesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncCategories();
      },
      () => {
        companyCategoriesReady = true;
        companyCategoriesDocs = [];
        syncCategories();
      }
    );

    const unsubLegacyCategories = onSnapshot(
      legacyCategoriesRef,
      (snapshot) => {
        legacyCategoriesReady = true;
        legacyCategoriesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncCategories();
      },
      () => {
        legacyCategoriesReady = true;
        legacyCategoriesDocs = [];
        syncCategories();
      }
    );

    return () => {
      unsubCompanyMain();
      unsubCompanyLiquidation();
      unsubCompanyOffice();
      unsubCompanyModules();
      unsubCompanyMonitor();
      unsubLegacySettings();
      unsubCompanyCategories();
      unsubLegacyCategories();
    };
  }, [activeCompanyId, activeCompanyName]);

  useEffect(() => {
    if (!db || !appId || !activeCompanyId) return undefined;

    let companyRoutesDocs = [];
    let legacyRoutesDocs = [];
    let companyRoutesReady = false;
    let legacyRoutesReady = false;

    let companyExpensesDocs = [];
    let legacyExpensesDocs = [];
    let companyExpensesReady = false;
    let legacyExpensesReady = false;

    const companyRoutesRef = collection(
      db,
      resolveRoutesCollectionPath(activeCompanyId)
    );
    const legacyRoutesRef = collection(db, `artifacts/${appId}/logisticsRoutes`);

    const companyExpensesRef = collection(
      db,
      resolveFleetExpensesCollectionPath(activeCompanyId)
    );
    const legacyExpensesRef = collection(db, `artifacts/${appId}/fleetExpenses`);

    const syncRoutes = () => {
      if (!companyRoutesReady || !legacyRoutesReady) return;

      const usingCompany = companyRoutesDocs.length > 0;
      setRoutesSource(usingCompany ? 'company' : 'legacy');
      setAllRoutes(sortRoutesDesc(usingCompany ? companyRoutesDocs : legacyRoutesDocs));
      setLoading(false);
      setError(null);
    };

    const syncExpenses = () => {
      if (!companyExpensesReady || !legacyExpensesReady) return;
      setAllFleetExpenses(
        sortExpensesDesc(
          companyExpensesDocs.length > 0 ? companyExpensesDocs : legacyExpensesDocs
        )
      );
    };

    const unsubCompanyRoutes = onSnapshot(
      companyRoutesRef,
      (snapshot) => {
        companyRoutesReady = true;
        companyRoutesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncRoutes();
      },
      (err) => {
        console.error('Error al obtener rutas de empresa:', err);
        companyRoutesReady = true;
        companyRoutesDocs = [];
        syncRoutes();
      }
    );

    const unsubLegacyRoutes = onSnapshot(
      legacyRoutesRef,
      (snapshot) => {
        legacyRoutesReady = true;
        legacyRoutesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncRoutes();
      },
      (err) => {
        console.error('Error al obtener rutas legacy:', err);
        legacyRoutesReady = true;
        legacyRoutesDocs = [];
        setError('No se pudo cargar la información de las rutas.');
        setLoading(false);
      }
    );

    const unsubCompanyExpenses = onSnapshot(
      companyExpensesRef,
      (snapshot) => {
        companyExpensesReady = true;
        companyExpensesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncExpenses();
      },
      (err) => {
        console.error('Error al obtener gastos de flota de empresa:', err);
        companyExpensesReady = true;
        companyExpensesDocs = [];
        syncExpenses();
      }
    );

    const unsubLegacyExpenses = onSnapshot(
      legacyExpensesRef,
      (snapshot) => {
        legacyExpensesReady = true;
        legacyExpensesDocs = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        syncExpenses();
      },
      (err) => {
        console.error('Error al obtener gastos de flota legacy:', err);
        legacyExpensesReady = true;
        legacyExpensesDocs = [];
        showMessage('Error al cargar los gastos de flota.', 'error');
        syncExpenses();
      }
    );

    return () => {
      unsubCompanyRoutes();
      unsubLegacyRoutes();
      unsubCompanyExpenses();
      unsubLegacyExpenses();
    };
  }, [activeCompanyId, showMessage]);

  useEffect(() => {
    if (!db || !appId || !activeCompanyId) return undefined;

    const companyProductsRef = collection(db, resolveStockProductsCollectionPath(activeCompanyId));
    const companyPurchaseLinesRef = collection(db, resolveStockPurchaseLinesCollectionPath(activeCompanyId));
    const companyMovementsRef = collection(db, resolveStockMovementsCollectionPath(activeCompanyId));

    const unsubProducts = onSnapshot(
      companyProductsRef,
      (snapshot) => {
        setStockProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (err) => {
        console.error('Error al obtener productos de stock para reportes:', err);
        setStockProducts([]);
      }
    );

    const unsubPurchaseLines = onSnapshot(
      companyPurchaseLinesRef,
      (snapshot) => {
        setStockPurchaseLines(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (err) => {
        console.error('Error al obtener líneas de compra para reportes:', err);
        setStockPurchaseLines([]);
      }
    );

    const unsubMovements = onSnapshot(
      companyMovementsRef,
      (snapshot) => {
        setStockMovements(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      (err) => {
        console.error('Error al obtener movimientos de stock para reportes:', err);
        setStockMovements([]);
      }
    );

    return () => {
      unsubProducts();
      unsubPurchaseLines();
      unsubMovements();
    };
  }, [activeCompanyId]);

  const handleSaveSettings = useCallback(async (newSettings) => {
    const normalizedTransportLiquidation = normalizeLiquidationSettings({
      ...newSettings,
      liquidationSettings: newSettings?.liquidationSettings || newSettings
    });
    const normalizedLegacyLiquidation = getNormalizedLiquidationSettings({
      ...newSettings,
      liquidationSettings: normalizedTransportLiquidation
    });

    const nextSettings = {
      ...settings,
      ...newSettings,
      costModel: normalizedLegacyLiquidation.costModel,
      pricePerKm: normalizedLegacyLiquidation.pricePerKm,
      costScales: normalizedLegacyLiquidation.costScales,
      liquidationSettings: normalizedTransportLiquidation
    };

    const mainSettingsRef = doc(db, resolveCompanyMainSettingsPath(activeCompanyId));
    const liquidationSettingsRef = doc(db, resolveCompanyLiquidationSettingsPath(activeCompanyId));
    const logisticsMonitorRef = doc(db, resolveLogisticsMonitorSettingsPath(activeCompanyId));

    const payloadBase = {
      companyId: activeCompanyId,
      companyName: activeCompanyName,
      updatedAt: new Date().toISOString()
    };

    try {
      setSettings(nextSettings);

      await Promise.all([
        setDoc(
          liquidationSettingsRef,
          {
            ...payloadBase,
            costModel: normalizedLegacyLiquidation.costModel,
            pricePerKm: normalizedLegacyLiquidation.pricePerKm,
            costScales: normalizedLegacyLiquidation.costScales,
            ...normalizedTransportLiquidation
          },
          { merge: true }
        ),
        setDoc(
          mainSettingsRef,
          {
            ...payloadBase,
            liquidationSettings: normalizedTransportLiquidation
          },
          { merge: true }
        ),
        setDoc(
          logisticsMonitorRef,
          {
            ...payloadBase,
            costModel: normalizedLegacyLiquidation.costModel,
            pricePerKm: normalizedLegacyLiquidation.pricePerKm,
            costScales: normalizedLegacyLiquidation.costScales,
            liquidationSettings: normalizedTransportLiquidation
          },
          { merge: true }
        )
      ]);

      const hasActiveRoutes = activeRoutes.length > 0;
      showMessage(
        hasActiveRoutes
          ? 'Configuración guardada. Las rutas activas mantienen la liquidación con la que fueron iniciadas. Los cambios se aplicarán a rutas nuevas.'
          : 'Configuración guardada.',
        'success'
      );
    } catch (err) {
      showMessage('Error al guardar la configuración.', 'error');
      console.error(err);
    }
  }, [activeCompanyId, activeCompanyName, activeRoutes.length, settings, showMessage]);

  const requestResetData = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const handleConfirmResetData = useCallback(async () => {
    setIsConfirmModalOpen(false);
    setLoading(true);

    try {
      const routesRef =
        routesSource === 'company'
          ? collection(db, resolveRoutesCollectionPath(activeCompanyId))
          : collection(db, `artifacts/${appId}/logisticsRoutes`);

      const snapshot = await getDocs(routesRef);
      const batch = writeBatch(db);

      snapshot.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref);
      });

      await batch.commit();

      showMessage(
        routesSource === 'company'
          ? `Se eliminó el historial de rutas de ${activeCompanyName}.`
          : 'Todo el historial legacy de rutas ha sido eliminado.',
        'success'
      );
    } catch (err) {
      console.error('Error al resetear datos:', err);
      showMessage('Error al eliminar el historial.', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, activeCompanyName, routesSource, showMessage]);

  const handleShowHistoryDetail = useCallback((route) => {
    setSelectedHistoryRoute(route);
    setShowHistoryDetail(true);
  }, []);

  const handleSelectRouteFromLiveMonitor = useCallback((route) => {
    setSelectedHistoryRoute(route);
    setShowHistoryDetail(true);
  }, []);

  return {
    loading,
    error,
    showHistoryDetail,
    selectedHistoryRoute,
    allRoutes,
    allFleetExpenses,
    fleetCategories,
    settings,
    isConfirmModalOpen,
    showYearlyHistory,
    activeRoutes,
    setShowHistoryDetail,
    setIsConfirmModalOpen,
    setShowYearlyHistory,
    handleSaveSettings,
    requestResetData,
    handleConfirmResetData,
    handleShowHistoryDetail,
    handleSelectRouteFromLiveMonitor,
    stockReportData: {
      products: stockProducts,
      purchaseLines: stockPurchaseLines,
      movements: stockMovements,
    }
  };
}
